import React from "react";

const RichTextRenderer = ({ content }) => {
  if (!content || !Array.isArray(content)) return null;

  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  const imageWidth = isMobile ? "350px" : "900px";

  const headingClassMap = {
    h1: "wise-secondary-gradient mb-3 mt-4",
    h2: "wise-secondary-gradient mb-3 mt-4",
    h3: "wise-secondary-gradient mb-2 mt-3",
    h4: "mb-2 mt-3",
  };

  const paragraphClass = "mb-3 text-white lh-lg"; // control spacing + color

  const renderNode = (node, key) => {
    if (!node || typeof node !== "object") return null;

    // Text node with formatting
    if (node.type === "text") {
      let el = node.text ?? "";

      if (node.format) {
        if (node.format & 1) el = <strong key={key}>{el}</strong>;
        if (node.format & 2) el = <em key={key}>{el}</em>;
        if (node.format & 4) el = <u key={key}>{el}</u>;
        if (node.format & 8) el = <s key={key}>{el}</s>;
        if (node.format & 16) {
          el = (
            <code
              key={key}
              className="bg-dark text-light px-2 py-1 rounded small"
            >
              {el}
            </code>
          );
        }
      }
      return el;
    }

    switch (node.type) {
      case "paragraph":
        return (
          <p key={key} className={paragraphClass}>
            {node.children?.map((child, idx) =>
              renderNode(child, `${key}-${idx}`),
            )}
          </p>
        );

      case "heading": {
        const Tag = node.tag || "h2";
        return (
          <Tag key={key} className={headingClassMap[Tag] || "mb-3 mt-4"}>
            {node.children?.map((child, idx) =>
              renderNode(child, `${key}-${idx}`),
            )}
          </Tag>
        );
      }

      case "blockquote":
        return (
          <blockquote
            key={key}
            className="border-start border-4 ps-3 fst-italic text-white  my-4"
          >
            {node.children?.map((child, idx) =>
              renderNode(child, `${key}-${idx}`),
            )}
          </blockquote>
        );

      case "code":
      case "codeblock":
        return (
          <pre
            key={key}
            className="bg-dark text-light p-3 rounded my-4 overflow-x-auto"
          >
            <code>
              {node.children?.map((child, idx) =>
                renderNode(child, `${key}-${idx}`),
              )}
            </code>
          </pre>
        );

      case "link":
      case "autolink": {
        const url = node.fields?.url || node.href || "#";
        const newTab = node.fields?.newTab ?? true;
        return (
          <a
            key={key}
            href={url}
            target={newTab ? "_blank" : "_self"}
            rel="noopener noreferrer"
            className="text-blue-600 underline"
          >
            {node.children?.map((child, idx) =>
              renderNode(child, `${key}-${idx}`),
            )}
          </a>
        );
      }

      case "list": {
        const Tag = node.listType === "bullet" ? "ul" : "ol";
        return (
          <Tag key={key} className="ps-4 mb-3">
            {node.children?.map((child, idx) =>
              renderNode(child, `${key}-${idx}`),
            )}
          </Tag>
        );
      }

      case "listitem":
        return (
          <li key={key} className="text-secondary mb-1">
            {node.children?.map((child, idx) =>
              renderNode(child, `${key}-${idx}`),
            )}
          </li>
        );

      case "linebreak":
        return <br key={key} />;

      case "upload": {
        const file = node.value || node.fields?.file;
        if (!file) return null;

        if (file.mimeType?.startsWith("image/")) {
          return (
            <img
              key={key}
              src={file.url}
              alt={file.alt || ""}
              className="my-4 rounded img-fluid"
              style={{ maxWidth: imageWidth }}
            />
          );
        }

        if (file.mimeType?.startsWith("video/")) {
          return (
            <video
              key={key}
              src={file.url}
              controls
              className="my-4 w-100 rounded"
            />
          );
        }

        return (
          <a
            key={key}
            href={file.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-secondary fw-semibold"
          >
            {file.filename || "Download file"}
          </a>
        );
      }

      case "youtubeEmbed": {
        const url = node.fields?.url;
        if (!url) return null;
        return (
          <div
            key={key}
            className="text-secondaryratio ratio-16x9 my-4 rounded overflow-hidden"
          >
            <iframe src={url} title="YouTube video" allowFullScreen />
          </div>
        );
      }

      default:
        return null;
    }
  };

  return <>{content.map((node, idx) => renderNode(node, idx))}</>;
};

export default RichTextRenderer;
